import clsx from "clsx";
import { motion } from "framer-motion";
import { ETransactionType } from "src/helpers/constants";
import { memo, useMemo, useState } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import Icon from "../Icon/Icon";
import useCurrentUser from "src/hooks/useCurrentUser";
import {
  formatEasyDate,
  formatLongDate,
  isEasyDate,
  rgbaObjectToString,
  transactionDescription,
} from "src/helpers/utils";
import ClientProviderInfoModal from "../ClientProviderInfoModal/ClientProviderInfoModal";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";
import useCurrencies from "src/hooks/useCurrencies";
import ShinyCard from "../BankCard/ShinyCard";
import SmartImage from "../SmartImage/SmartImage";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";
import useUiPreferences from "src/store/useUiPreferences";
import CheckBox from "../CheckBox/CheckBox";
import useColors from "src/hooks/useColors";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "src/constants/icons";
import { IButton } from "../Button/Button.types";

interface IStandardTransactionCard {
  transaction: any;
  index: number;
  customAnimationDirection?: "x" | "y" | null;
  onSelect?: ({
    transaction,
    checked,
  }: {
    transaction: any;
    checked: boolean;
  }) => void;
  checked?: boolean;
  onClick?: (e: any) => void;
  buttons?: IButton[];
}

const StandardTransactionCard: React.FC<IStandardTransactionCard> = ({
  transaction,
  index,
  customAnimationDirection = null,
  onSelect = null,
  checked = false,
  onClick,
  buttons = [],
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { getIncomeColors, getExpensesColors } = useColors();
  const { t } = useTranslation(["common", "misc"]);
  const { getCurrencyDataByCode } = useCurrencies();
  const { isMobile } = useMobile();
  const { getSourceCustomSettings } = useAllTransactions();
  const [showSourceModal, setShowSourceModal] = useState<boolean>(false);

  const { getAccountById } = useCurrentUser();

  const sourceInfo = useMemo(() => {
    return getSourceCustomSettings(transaction?.payTo);
  }, [transaction, getSourceCustomSettings]);

  const accountInfo = useMemo(() => {
    return getAccountById(transaction?.account);
  }, [transaction, getAccountById]);

  const currencySymbol = useMemo(() => {
    return (
      getCurrencyDataByCode({ code: accountInfo?.currency })?.symbol_native ||
      ""
    );
  }, [accountInfo, getCurrencyDataByCode]);

  const description = useMemo(() => {
    return transactionDescription(transaction);
  }, [transaction]);

  const animationDirection = useMemo(() => {
    return customAnimationDirection
      ? customAnimationDirection
      : isMobile
        ? "y"
        : "x";
  }, [isMobile]);

  const getCheckboxStyle = (transactionType: ETransactionType) => {
    switch (transactionType) {
      case ETransactionType.INCOME: {
        return {
          color: getIncomeColors().string.background,
        };
      }
      case ETransactionType.EXPENSE: {
        return {
          color: getExpensesColors().string.background,
        };
      }
      default: {
        return {};
      }
    }
  };

  return (
    <>
      <motion.div
        className={clsx([
          "flex",
          "w-full",
          "group",
          "bg-gray-50 dark:bg-gray-900/30",
          "p-2",
          "md:p-2",
          "rounded",
          "hover:rounded",
          "border-0",
          "border-gray-50",
          "dark:border-gray-900/50",
          "hover:bg-gray-100",
          "hover:dark:bg-gray-900",
        ])}
        {...(enableAnimations && {
          initial: {
            opacity: 0,
            [animationDirection]: index % 2 === 0 ? -100 : 100,
          },
          animate: { opacity: 1, [animationDirection]: 0 },
          transition: {
            duration: 0.2,
            ease: "easeOut",
            // delay: index * 0.1,
          },
        })}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "md:gap-2",
            "gap-2",
            {
              "cursor-pointer": !!onClick,
            },
          ])}
          onClick={(e: any) => {
            onClick?.(e);
          }}
        >
          <div
            className={clsx([
              "flex",
              "flex-row",
              "gap-2",
              "md:gap-2",
              "items-center",
            ])}
          >
            <div
              className={clsx([
                "!h-[50px]",
                "!w-[50px]",
                "!min-h-[50px]",
                "!min-w-[50px]",
                "bg-checkers",
                "md:!h-[50px]",
                "md:!w-[50px]",
                "md:!min-h-[50px]",
                "md:!min-w-[50px]",
                "rounded-full",
                "overflow-hidden",
                "inline-flex",
                "items-center",
                "justify-center",
                "cursor-pointer",
                "relative",
              ])}
              title={t("common:tooltips.contact_information")}
              onClick={(e: any) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                setShowSourceModal(true);
              }}
              style={
                sourceInfo?.picture
                  ? {}
                  : {
                      background: rgbaObjectToString(sourceInfo?.bg),
                      color: rgbaObjectToString(sourceInfo?.color),
                    }
              }
            >
              <ShinyCard>
                {sourceInfo?.picture ? (
                  <SmartImage
                    src={sourceInfo?.picture?.thumbnailUrl}
                    className={clsx(["bg-checkers", "!w-full", "!h-full"])}
                    alt={transaction?.payTo}
                  />
                ) : (
                  <div
                    className={clsx([
                      "flex",
                      "!h-full",
                      "!w-full",
                      "items-center",
                      "justify-center",
                    ])}
                  >
                    <Icon
                      name={sourceInfo?.icon}
                      size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                    />
                  </div>
                )}
              </ShinyCard>
            </div>
            <div className={clsx(["flex", "flex-col", "w-full", "truncate"])}>
              <div
                className={clsx([
                  "text-xs",
                  "md:text-xs",
                  "truncate",
                  "font-medium",
                ])}
              >
                {transaction?.payTo}
              </div>
              <div className={clsx(["text-xs", "md:text-xs", "truncate"])}>
                {description}
              </div>

              <div className={clsx(["text-xs", "md:text-xs", "truncate"])}>
                {transaction?.nextPayment
                  ? isEasyDate(transaction?.nextPayment)
                    ? formatEasyDate({ dateToFormat: transaction?.nextPayment })
                    : formatLongDate(transaction?.nextPayment, true)
                  : isEasyDate(transaction?.date)
                    ? formatEasyDate({ dateToFormat: transaction?.date })
                    : formatLongDate(transaction?.date, true)}
              </div>
            </div>
            <div
              className={clsx([
                "flex",
                "flex-col",
                "justify-center",
                "items-end",
              ])}
              onClick={(e: any) => {
                if (onSelect) {
                  e?.stopPropagation?.();
                  e?.preventDefault?.();
                  onSelect?.({
                    transaction,
                    checked: !checked,
                  });
                }
              }}
            >
              {onSelect ? (
                <CheckBox
                  checked={checked}
                  onChange={(value: boolean) => {
                    onSelect?.({
                      transaction,
                      checked: value,
                    });
                  }}
                  iconStyles={getCheckboxStyle(transaction.type)}
                />
              ) : null}
              <CurrencyBadge
                value={transaction?.total}
                currencySymbol={currencySymbol}
                kind={transaction?.type}
              />
            </div>
          </div>
          {buttons?.length > 0 && (
            <div>
              <ButtonGroup buttons={buttons} />
            </div>
          )}
        </div>
      </motion.div>

      {showSourceModal && (
        <ClientProviderInfoModal
          clientProvider={transaction?.payTo}
          onClose={() => setShowSourceModal(false)}
        />
      )}
    </>
  );
};

export default memo(StandardTransactionCard);
